/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import FileModel from '../../../models/FileModel';

import FileActions from '../../appAuthorized/redux/FileActions';

import { AppNavSubmitButton, AppNavCancelButton } from '../../components/appNav/appNavBits/AppNavButtons';
import { FileMetadata } from './components/FileMetadata';
import { AppNavFileDetail } from './components/AppNavFileDetail';
import RouteNames from '../../appAuthorized/RouteNames';
import { Card, Icons } from '../../../bphComponents/bundle';
import COLORS from '../../styles/Colors';
import { FileShareCards } from '../components/attributes/ShareCardSelection';
import { DownloadingProgressBar } from '../../components/ProgressIndicator';
import ShareCardModel from '../../../models/ShareCardModel';
import ContentWrapper from '../../components/ContentWrapper';

import { useShareCardListForActiveProfile } from '../../hooks/useShareCard';
import { useAttachments } from '../../hooks/useAttachments';
import { useNavigate } from '../../hooks/useNavigate';
import { queryCache } from 'react-query';
import { useUIState } from '../../hooks/useUIState';

const FileUpload = (props) => {
  const { file, rawFile, onSuccess, attachmentType, parentId  } = props;
  const fileId = -1;

  const isAttachment = Boolean(attachmentType && parentId);
  const [fileData, setFileData] = React.useState({ id: fileId, ...file.data });
  const newFile = new FileModel(fileData);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [, navActions] = useNavigate();
  const [, attachmentActions] = useAttachments();
  const [{ activeProfileId }] = useUIState();

  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [uploadRequest, setUploadRequest] = React.useState(null);
  const [scData, scActions] = useShareCardListForActiveProfile();

  const [shareCards, setShareCards] = React.useState(() => {
    return scActions.shareCardsAvailableForFiles();
  });

  React.useEffect(() => {
    setShareCards(scActions.shareCardsAvailableForFiles());
  }, [scData.isLoaded]);

  function finishUpload(newFileId) {
    onSuccess();

    queryCache.invalidateQueries('files');

    if (isAttachment) {
      navActions.replace(RouteNames.file(newFileId, attachmentType, parentId));
    } else {
      navActions.replace(RouteNames.file(newFileId));
    }
  }

  function uploadComplete(createdFile) {
    if (!createdFile) { return; }
    const newFileId = createdFile.data.id;
    setUploadRequest(null);

    queryCache.setQueryData(['files', activeProfileId, newFileId], {
      data: createdFile.data,
      isLoaded: true,
    });
    queryCache.invalidateQueries('shareCards');
    queryCache.invalidateQueries('files');

    if (isAttachment) {
      attachmentActions
        .apiAttachFile(newFileId, attachmentType, parentId)
        .then(() => finishUpload(newFileId));
    } else {
      finishUpload(newFileId);
    }
  }

  const onCancel = () => {
    if (uploadRequest) {
      uploadRequest.abort();
      setUploadRequest(null);
    }

    FileActions.cancelChanges();
    navActions.goBack();
  };

  const onSubmit = () => {
    if (!fileData.name) { return; }
    setIsSubmitting(true);
    setUploadProgress(1);

    // add included share cards to fileData
    // special attribute for file creation
    const newData = shareCards.reduce((acc, val) => {
      if (ShareCardModel.includesFile(val, fileId)) {
        return [...acc, val.id];
      }
      return acc;
    }, []);

    const newFileData = {
      ...fileData,
      included_share_cards: newData,
    };

    const uploadRequest = FileActions.createFile(
      newFileData,
      rawFile,
      newProgress => {
        setUploadProgress(Math.round(newProgress));
      },
      uploadComplete,
    );

    setUploadRequest(uploadRequest);
  };

  const updateShareCard = (scData) => {
    const newShareCards = shareCards.map(d => {
      return d.id !== scData.id ? d : scData;
    });
    setShareCards(newShareCards);
  };

  return (
    <div style={{ width: '100%' }}>

      <AppNavFileDetail>
        <AppNavCancelButton
          onClick={onCancel}
          color="white"
        />
        {
          isSubmitting
          ? (
            <div className="font-footnote font-color-gsLightest">{uploadProgress}%</div>
          ) : (
            <AppNavSubmitButton
              onClick={onSubmit}
              isDisabled={false}
            />
          )
        }
      </AppNavFileDetail>

      <DownloadingProgressBar
        progress={uploadProgress}
        totalTime={300}
        style={{ position: 'absolute', width: '100%', top: -4 }}
      />

      <ContentWrapper>
        <FileMetadata file={newFile} setFileData={setFileData} showComments={false} nameHasAutoFocus={true} />

        <Card.ExternalCardTitle className="font-color-gsLightest">
          <FormattedMessage id="healthEntry.addToShareCards.name.text" />
          <Icons.IconShare.Small color={COLORS.gsLightest} />
        </Card.ExternalCardTitle>

        <FileShareCards
          fileId={fileId}
          setFileData={setFileData}
          updateShareCard={updateShareCard}
          shareCardList={shareCards}
        />
      </ContentWrapper>
    </div>
  );
};

export default FileUpload;
